/**
 * RecommendedLessonsCard — "Recommended for you" on the Practice tab.
 *
 * Surfaces GET /api/lessons/recommended, which ranks lessons against the
 * learner's accumulated errorPatterns. Each row states WHY it is there — the
 * error type and how many times they have made it — because the reason is the
 * motivating part, not the lesson title.
 *
 * Distinct from the in-chat "STUDY THIS" references in FeedbackCard: those
 * react to the errors in the message just sent and are not persisted, so they
 * vanish on reload. This reflects the learner's whole history.
 *
 * Renders nothing when there is nothing to recommend (new learner, or the
 * fetch failed). Like SoundMapSummaryCard, the Practice screen must never grow
 * an error surface for a secondary card.
 *
 * @see docs/specs/mobile/practice.mobile.md
 * @see docs/specs/api/lessons.api.md
 */

import React, { useCallback } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../ui/Ionicons';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { useCompetenceFocusSession } from '../../hooks/useCompetenceFocusSession';
import { AIConsentModal } from '../AIConsentModal';
import { useLessonStore } from '../../store/lessonStore';
import {
  selectRecommendations,
  selectFetchRecommendations,
} from '../../store/lessonSelectors';
import type { RecommendedLesson } from '../../types/lesson';
import {
  competenceFocusA11yLabel,
  formatReasonLine,
  recommendationA11yLabel,
} from './recommendedText';

const RecommendedRow = React.memo(function RecommendedRow({
  item,
  showDivider,
  onPress,
  onFocusPress,
  focusStarting,
}: {
  item: RecommendedLesson;
  showDivider: boolean;
  onPress: (slug: string, title?: string) => void;
  onFocusPress: (competenceSlug: string) => void;
  focusStarting: boolean;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const handlePress = useCallback(() => {
    onPress(item.lesson.slug, item.lesson.title);
  }, [onPress, item.lesson.slug, item.lesson.title]);

  // Non-null only for grammar competences: the server decides which error types
  // are practisable in conversation (pronunciation belongs to the Sound Map,
  // listening has no production to correct), so the row shows the action only
  // where a focus would actually take effect.
  const competenceSlug = item.competenceSlug ?? null;

  const handleFocusPress = useCallback(() => {
    if (competenceSlug) onFocusPress(competenceSlug);
  }, [onFocusPress, competenceSlug]);

  return (
    <View
      style={[
        styles.row,
        showDivider && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: theme.border },
      ]}
    >
      {/* Study the lesson — the row's primary, unchanged action. */}
      <TouchableOpacity
        testID={`practice-recommended-${item.lesson.slug}`}
        accessibilityRole="button"
        accessibilityLabel={recommendationA11yLabel(t, item)}
        onPress={handlePress}
        activeOpacity={0.7}
        style={styles.rowBody}
      >
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {item.lesson.title}
        </Text>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
        >
          {formatReasonLine(t, item)}
        </Text>
      </TouchableOpacity>

      {/* Practise it in conversation — a sibling action, not a replacement.
          Reading about the mistake and being made to produce the structure are
          different exercises, and the learner is the one who knows which they
          need. Icon-sized so the row stays a nudge rather than a menu. */}
      {competenceSlug !== null && (
        <TouchableOpacity
          testID={`practice-recommended-focus-${competenceSlug}`}
          accessibilityRole="button"
          accessibilityLabel={competenceFocusA11yLabel(t, item)}
          accessibilityState={{ disabled: focusStarting }}
          onPress={handleFocusPress}
          disabled={focusStarting}
          activeOpacity={0.7}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={[styles.focusButton, { backgroundColor: theme.inputBg }]}
        >
          {focusStarting ? (
            <ActivityIndicator size="small" color={theme.primary} />
          ) : (
            <Ionicons
              name="chatbubbles-outline"
              size={Math.round(FS_SM * fontScale) + 4}
              color={theme.primary}
            />
          )}
        </TouchableOpacity>
      )}

      <Ionicons
        name="chevron-forward"
        size={Math.round(FS_SM * fontScale) + 4}
        color={theme.textSecondary}
      />
    </View>
  );
});

export const RecommendedLessonsCard = React.memo(function RecommendedLessonsCard({
  onLessonPress,
}: {
  onLessonPress: (slug: string, title?: string) => void;
}) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const recommendations = useLessonStore(selectRecommendations);
  const fetchRecommendations = useLessonStore(selectFetchRecommendations);
  const {
    startFocusedSession,
    startingSlug,
    consentModalVisible,
    acceptConsent,
    declineConsent,
  } = useCompetenceFocusSession();

  // Refresh on focus: error patterns accumulate while the learner is in chat,
  // so coming back to Practice should reflect what just happened.
  useFocusEffect(
    useCallback(() => {
      void fetchRecommendations();
    }, [fetchRecommendations]),
  );

  // Nothing to recommend yet, or the fetch failed — stay out of the way.
  // Owning the outer spacing (rather than taking it from a wrapper on the
  // screen) is what lets this collapse to nothing instead of leaving a gap.
  if (recommendations.length === 0) return null;

  return (
    <View
      testID="practice-recommended-card"
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
    >
      <Text
        allowFontScaling={false}
        accessibilityRole="header"
        style={{ fontSize: FS_MD * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
      >
        {t('practice.recommended.title')}
      </Text>
      <Text
        allowFontScaling={false}
        style={{
          fontSize: FS_XS * fontScale,
          color: theme.textSecondary,
          marginTop: 2,
          marginBottom: 4,
          ...ffs,
        }}
      >
        {t('practice.recommended.subtitle')}
      </Text>

      {recommendations.map((item, index) => (
        <RecommendedRow
          key={`${item.errorType}-${item.lesson.slug}`}
          item={item}
          showDivider={index > 0}
          onPress={onLessonPress}
          onFocusPress={startFocusedSession}
          // Explicit null guard: `startingSlug === item.competenceSlug` alone is
          // true for every non-competence row while nothing is starting.
          focusStarting={startingSlug !== null && startingSlug === item.competenceSlug}
        />
      ))}

      <AIConsentModal
        visible={consentModalVisible}
        onAccept={acceptConsent}
        onDecline={declineConsent}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    // Matches the $5 / $3 spacing the sibling Practice cards get from their
    // wrapper Box; kept local so the empty case leaves no gap behind.
    marginHorizontal: 20,
    marginBottom: 12,
    padding: 14,
    borderRadius: 16,
    borderWidth: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 10,
    marginTop: 6,
  },
  rowBody: {
    flex: 1,
    marginRight: 8,
  },
  focusButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
});
